import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineCash } from 'react-icons/hi';
import { MdSearch } from 'react-icons/md';
import { BillingCycle, VAT_RATE } from '@views/Invoice/invoiceTypes';
import {
  getInvoiceCost,
  getInvoiceVatCost,
  getTheViewersInvoice,
  isViewedByTheShipper,
} from '@views/Invoice/invoiceHelper';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { INVOICE_LIST } from '../../constants';
import { useAuthHttp } from '../../hooks';
import { useAuth } from '../../context';
import { UserRoleService } from '../../services';

export function Payments() {
  const authHttp = useAuthHttp();
  const { user } = useAuth();
  const { isWarehouseClient } = UserRoleService();
  const navigate = useNavigate();
  const [billingCycles, setBillingCycles] = useState<BillingCycle[]>([]);
  const [isInvoicesLoading, setIsInvoicesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const invoiceSubtotals = billingCycles.map((billingCycle) => {
    const invoice = getTheViewersInvoice(billingCycle, user?.businessGroupId);
    const subTotalAmount = getInvoiceCost(invoice);
    const subTotalVatAmount = getInvoiceVatCost(invoice);

    return {
      invoiceNumber: invoice?.invoiceNumber,
      subTotalAmount,
      subTotalVatAmount,
    };
  });

  const fetchInvoices = async () => {
    setIsInvoicesLoading(true);
    try {
      const { data } = await authHttp.post(
        INVOICE_LIST,
        {
          statuses: ['Paid'],
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data) {
        setBillingCycles(data.billingCycles);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsInvoicesLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter payments based on search query
  const filteredBillingCycles = billingCycles.filter((billingCycle) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const invoice = getTheViewersInvoice(billingCycle, user?.businessGroupId);
    return (
      (invoice?.invoiceNumber || '').toLowerCase().includes(query) ||
      (billingCycle.shipperBusinessName || '').toLowerCase().includes(query) ||
      (billingCycle.lspBusinessName || '').toLowerCase().includes(query)
    );
  });

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Payments', href: '/payments', current: true },
  ];

  // Calculate totals for filtered results
  const filteredSubtotals = filteredBillingCycles.map((billingCycle) => {
    const invoice = getTheViewersInvoice(billingCycle, user?.businessGroupId);
    const subTotalAmount = getInvoiceCost(invoice);
    const subTotalVatAmount = getInvoiceVatCost(invoice);
    return { subTotalAmount, subTotalVatAmount };
  });

  const subTotalSum = filteredSubtotals.reduce(
    (sum, s) => sum + s.subTotalAmount,
    0
  );
  const vatTotalSum = filteredSubtotals.reduce(
    (sum, s) => sum + s.subTotalVatAmount,
    0
  );
  const grandTotal = subTotalSum + vatTotalSum;

  // Get currency symbol from first billing cycle
  const firstInvoice =
    billingCycles.length > 0
      ? getTheViewersInvoice(billingCycles[0], user?.businessGroupId)
      : undefined;
  const currencySymbol = firstInvoice?.lines[0]?.currencySymbol || '£';

  return (
    <>
      <PageHeader header='Payments' pages={pages} />
      <main className='flex flex-col gap-6 min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] p-4 lg:pt-4 lg:px-8 lg:pb-8'>
        {/* Info Banner */}
        <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
          <p className='text-sm text-blue-800'>
            <span className='font-medium'>FLOX PAYMENT:</span> Transaction
            details for the services provided between{' '}
            <span className='font-bold'>
              {moment().subtract(2, 'weeks').format('DD/MM/YY')}
            </span>{' '}
            and <span className='font-bold'>{moment().format('DD/MM/YY')}</span>
          </p>
        </div>

        {/* Summary Cards */}
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
          {/* Total Payments */}
          <div className='bg-white rounded-lg border border-gray-200 p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm text-gray-500'>Total Payments</p>
                <p className='text-2xl font-semibold text-gray-900 mt-1'>
                  {billingCycles.length}
                </p>
              </div>
              <div className='p-2 bg-gray-100 rounded-lg'>
                <HiOutlineCash className='w-6 h-6 text-gray-500' />
              </div>
            </div>
          </div>

          {/* Total Amount */}
          <div className='bg-white rounded-lg border border-gray-200 p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm text-gray-500'>
                  Total Amount (excl. VAT)
                </p>
                <p className='text-2xl font-semibold text-gray-900 mt-1'>
                  {currencySymbol}
                  {invoiceSubtotals
                    .reduce((sum, s) => sum + s.subTotalAmount, 0)
                    .toFixed(2)}
                </p>
              </div>
              <div className='p-2 bg-gray-100 rounded-lg'>
                <HiOutlineCash className='w-6 h-6 text-gray-500' />
              </div>
            </div>
          </div>

          {/* Grand Total */}
          <div className='bg-green-50 rounded-lg border border-green-200 p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm text-green-700'>
                  Grand Total (incl. VAT)
                </p>
                <p className='text-2xl font-semibold text-green-700 mt-1'>
                  {currencySymbol}
                  {(
                    invoiceSubtotals.reduce(
                      (sum, s) => sum + s.subTotalAmount,
                      0
                    ) +
                    invoiceSubtotals.reduce(
                      (sum, s) => sum + s.subTotalVatAmount,
                      0
                    )
                  ).toFixed(2)}
                </p>
              </div>
              <div className='p-2 bg-green-100 rounded-lg'>
                <HiOutlineCash className='w-6 h-6 text-green-600' />
              </div>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
          {/* Search/Filters Row */}
          <div className='flex items-center gap-4 p-4 border-b border-gray-200'>
            {/* Search input */}
            <div className='relative flex-1 max-w-sm'>
              <MdSearch className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                placeholder='Search payments...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              />
            </div>
          </div>

          {/* Table */}
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr className='border-b border-gray-200 bg-gray-50'>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16'>
                    #
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Date
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {isWarehouseClient(user) ? 'Provider' : 'Customer'}
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Invoice
                  </th>
                  <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Amount
                  </th>
                  <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    VAT @{VAT_RATE}%
                  </th>
                  <th className='px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                {isInvoicesLoading && (
                  <tr>
                    <td colSpan={7} className='text-center py-12'>
                      <div className='flex flex-col items-center gap-3'>
                        <FloxSpinner size='md' />
                        <p className='text-gray-500 text-sm'>
                          Loading payments...
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {filteredBillingCycles.length === 0 && !isInvoicesLoading && (
                  <tr>
                    <td colSpan={7} className='text-center py-12'>
                      <div className='flex flex-col items-center gap-3'>
                        <HiOutlineCash className='w-12 h-12 text-gray-300' />
                        <p className='text-gray-900 font-medium'>
                          No payments found
                        </p>
                        <p className='text-gray-500 text-sm'>
                          Payments will appear here once invoices are paid
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {filteredBillingCycles.map((billingCycle, index) => {
                  const invoice = getTheViewersInvoice(
                    billingCycle,
                    user?.businessGroupId
                  );

                  if (!invoice) {
                    return null;
                  }

                  const subTotalAmount = getInvoiceCost(invoice);
                  const subTotalVatAmount = getInvoiceVatCost(invoice);

                  return (
                    <tr
                      key={invoice.id}
                      className='hover:bg-gray-50 cursor-pointer'
                      onClick={() => {
                        navigate(`/invoice/${billingCycle.id}`);
                      }}
                    >
                      <td className='px-4 py-4 text-sm text-gray-500'>
                        {index + 1}
                      </td>
                      <td className='px-4 py-4'>
                        <span className='text-sm text-gray-900'>
                          {invoice.paidAt
                            ? moment(invoice.paidAt).format('D MMM YYYY')
                            : '-'}
                        </span>
                      </td>
                      <td className='px-4 py-4'>
                        <span className='text-sm text-gray-900'>
                          {isViewedByTheShipper(
                            billingCycle,
                            user?.businessGroupId
                          )
                            ? billingCycle.lspBusinessName
                            : billingCycle.shipperBusinessName}
                        </span>
                      </td>
                      <td className='px-4 py-4'>
                        <span className='text-sm font-medium text-blue-600'>
                          {invoice.invoiceNumber}
                        </span>
                      </td>
                      <td className='px-4 py-4 text-right'>
                        <span className='text-sm font-medium text-gray-900'>
                          {invoice.lines[0]?.currencySymbol}
                          {subTotalAmount.toFixed(2)}
                        </span>
                      </td>
                      <td className='px-4 py-4 text-right'>
                        <span className='text-sm font-medium text-gray-900'>
                          {invoice.lines[0]?.currencySymbol}
                          {subTotalVatAmount.toFixed(2)}
                        </span>
                      </td>
                      <td className='px-4 py-4 text-center'>
                        <span className='inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'>
                          Completed
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {/* Subtotal Row */}
                {filteredBillingCycles.length > 0 && (
                  <tr className='bg-gray-50'>
                    <td className='px-4 py-4' colSpan={4}>
                      <span className='sr-only'>Empty cells</span>
                    </td>
                    <td className='px-4 py-4 text-right'>
                      <div className='flex flex-col'>
                        <span className='text-xs text-gray-500'>Sub Total</span>
                        <span className='text-sm font-semibold text-gray-900'>
                          {currencySymbol}
                          {subTotalSum.toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className='px-4 py-4 text-right'>
                      <div className='flex flex-col'>
                        <span className='text-xs text-gray-500'>VAT Total</span>
                        <span className='text-sm font-semibold text-gray-900'>
                          {currencySymbol}
                          {vatTotalSum.toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className='px-4 py-4'>
                      <span className='sr-only'>Empty cell</span>
                    </td>
                  </tr>
                )}

                {/* Grand Total Row */}
                {filteredBillingCycles.length > 0 && (
                  <tr className='bg-green-50'>
                    <td className='px-4 py-4' colSpan={4}>
                      <span className='sr-only'>Empty cells</span>
                    </td>
                    <td className='px-4 py-4 text-right' colSpan={2}>
                      <div className='flex flex-col'>
                        <span className='text-xs text-green-700'>
                          Grand Total (incl. VAT)
                        </span>
                        <span className='text-sm font-bold text-green-800'>
                          {currencySymbol}
                          {grandTotal.toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className='px-4 py-4'>
                      <span className='sr-only'>Empty cell</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer with count */}
          {filteredBillingCycles.length > 0 && (
            <div className='px-4 py-3 border-t border-gray-200 text-sm text-gray-500'>
              Showing {filteredBillingCycles.length} of {billingCycles.length}{' '}
              payments
            </div>
          )}
        </div>
      </main>
    </>
  );
}
